import { isUnrestrictedAdminRole, normalizeRoleSlug } from "../../../public/utils/roleAccess";
import {
  MEMBERSHIPS_APPLY_SUBMODULE_SLUG,
  MEMBERSHIPS_CONTROL_SUBMODULE_SLUG,
  MEMBERSHIPS_LIST_SUBMODULE_SLUG,
  MEMBERSHIPS_PAYMENTS_SUBMODULE_SLUG,
  MEMBERSHIPS_RENEWALS_SUBMODULE_SLUG,
} from "./membershipPermissionSlugs";

function permRow(permMap, slug) {
  const row = permMap?.[slug];
  return row && typeof row === "object" ? row : {};
}

function emptyPerms() {
  return {
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canManageMasters: false,
    canDeleteMasters: false,
    canExport: false,
    isAdmin: false,
  };
}

function fullPerms({ isAdmin = true } = {}) {
  return {
    canView: true,
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canManageMasters: true,
    canDeleteMasters: true,
    canExport: true,
    isAdmin,
  };
}

/**
 * Staff membership screen permissions from User builder matrix.
 * Do not use for student portal (my-memberships).
 */
export function buildMembershipPermissionsForScreen(
  permMap = {},
  roleSlug = "",
  screenSlug = "",
  { isUnrestricted = false } = {},
) {
  const slug = normalizeRoleSlug(screenSlug);
  if (isUnrestricted || isUnrestrictedAdminRole(roleSlug)) {
    return fullPerms({ isAdmin: true });
  }

  const row = permRow(permMap, slug);
  const canView = Boolean(row.view);
  const canCreate = Boolean(row.create);
  const canEdit = Boolean(row.edit);
  const canDelete = Boolean(row.delete);

  const isControl = slug === MEMBERSHIPS_CONTROL_SUBMODULE_SLUG;

  return {
    canView,
    canCreate,
    canEdit,
    canDelete,
    canManageMasters: isControl && canView,
    canDeleteMasters: isControl && canDelete,
    canExport: canView,
    isAdmin: false,
  };
}

export function buildMembershipPermissions(
  permMap = {},
  roleSlug = "",
  { isUnrestricted = false } = {},
) {
  if (isUnrestricted || isUnrestrictedAdminRole(roleSlug)) {
    return fullPerms({ isAdmin: true });
  }

  const slugs = [
    MEMBERSHIPS_CONTROL_SUBMODULE_SLUG,
    MEMBERSHIPS_APPLY_SUBMODULE_SLUG,
    MEMBERSHIPS_RENEWALS_SUBMODULE_SLUG,
    MEMBERSHIPS_LIST_SUBMODULE_SLUG,
    MEMBERSHIPS_PAYMENTS_SUBMODULE_SLUG,
  ];

  let canView = false;
  let canCreate = false;
  let canEdit = false;
  let canDelete = false;
  for (const slug of slugs) {
    const row = permRow(permMap, slug);
    if (row.view) canView = true;
    if (row.create) canCreate = true;
    if (row.edit) canEdit = true;
    if (row.delete) canDelete = true;
  }

  if (!canView && !canCreate && !canEdit && !canDelete) {
    return emptyPerms();
  }

  return {
    canView,
    canCreate,
    canEdit,
    canDelete,
    canManageMasters: Boolean(permRow(permMap, MEMBERSHIPS_CONTROL_SUBMODULE_SLUG).view),
    canDeleteMasters: Boolean(permRow(permMap, MEMBERSHIPS_CONTROL_SUBMODULE_SLUG).delete),
    canExport: canView,
    isAdmin: false,
  };
}
